import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const DashboardLayout = ({ children, onLogout, user }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [darkMode, setDarkMode] = useState(false);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sidebarMinimized, setSidebarMinimized] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  const API_BASE_URL = "https://dairy.server.statify.co.ke";

  // Define categories with their tabs
  const sidebarCategories = [
    {
      key: 'dashboard',
      label: 'Dashboard',
      icon: '📊',
      path: '/dashboard',
      type: 'single'
    },
    {
      key: 'animals',
      label: 'Animals',
      icon: '🐄',
      type: 'category',
      tabs: [
        { key: 'animals_draft', label: 'Draft', icon: '📝', path: '/animals/draft' },
        { key: 'animals_active', label: 'Active', icon: '✅', path: '/animals/active' },
        { key: 'animals_inactive', label: 'Inactive', icon: '🚫', path: '/animals/inactive' }
      ]
    },
    {
      key: 'production',
      label: 'Production',
      icon: '📦',
      type: 'category',
      tabs: [
        { key: 'milking_sessions', label: 'Milking Sessions', icon: '🥛', path: '/milking-sessions' },
        { key: 'production_batches', label: 'Production Batches', icon: '📦', path: '/production-batches' },
        { key: 'calving_events', label: 'Calving Events', icon: '👶', path: '/calving-events' },
        { key: 'reproduction_events', label: 'Reproduction', icon: '🔬', path: '/reproduction-events' },
        { key: 'production_report', label: 'Production Report', icon: '📊', path: '/production_report' }
      ]
    },
    {
      key: 'health',
      label: 'Health',
      icon: '🏥',
      type: 'category',
      tabs: [
        { key: 'health_records', label: 'Health Records', icon: '🏥', path: '/health-records' },
        { key: 'medications', label: 'Medications', icon: '💊', path: '/medications' },
        { key: 'medication_usage', label: 'Medication Usage', icon: '📋', path: '/medication-usage' }
      ]
    },
    {
      key: 'sales',
      label: 'Sales',
      icon: '💰',
      type: 'category',
      tabs: [
        { key: 'customers', label: 'Customers', icon: '👥', path: '/customer-form' },
        { key: 'products',  label: 'Products',  icon: '🛍️', path: '/product-form' },
        { key: 'invoices',  label: 'Sales invoice', icon: '🧾', path: '/invoice-form' },
        { key: 'receipts',  label: 'Receipts', icon: '💵', path: '/receipts' }
      ]
    },
    {
      key: 'hr',
      label: 'HR',
      icon: '👥',
      type: 'category',
      tabs: [
        { key: 'hr', label: 'Employees', icon: '👥', path: '/employees' },
        { key: 'payroll', label: 'Payroll', icon: '💰', path: '/payroll' }
      ]
    },
    {
      key: 'setups',
      label: 'Setups',
      icon: '⚙️',
      type: 'category',
      tabs: [
        { key: 'locations', label: 'Locations', icon: '📍', path: '/locations' },
        { key: 'payroll Setup', label: 'Payroll Setup', icon: '💰', path: '/payroll-setup' }
      ]
    },
    {
      key: 'admin',
      label: 'Admin',
      icon: '👥',
      type: 'category',
      tabs: [
        { key: 'users', label: 'Users', icon: '👥', path: '/users' },
        { key: 'roles', label: 'Roles', icon: '🔐', path: '/roles' },
        { key: 'audit_logs', label: 'Audit Logs', icon: '📝', path: '/audit-logs' }
      ]
    }
  ];

  // Updated color scheme with new colors
  const colors = {
    light: {
      primary: '#024A59', // Dark Teal
      secondary: '#FFA916', // Orange
      accent: '#4169E1', // Royal Blue
      background: '#F8F9FA',
      surface: '#FFFFFF',
      text: '#2C3E50',
      textSecondary: '#6C757D',
      border: '#E9ECEF',
      hover: '#F1F3F4'
    },
    dark: {
      primary: '#024A59', // Same teal for consistency
      secondary: '#FFA916', // Orange
      accent: '#64B5F6', // Light Blue
      background: '#121212',
      surface: '#1E1E1E',
      text: '#E0E0E0',
      textSecondary: '#B0B0B0',
      border: '#333333',
      hover: '#2A2A2A'
    }
  };

  const currentColors = darkMode ? colors.dark : colors.light;

  // Get token from localStorage
  const getToken = () => {
    return localStorage.getItem('token') || localStorage.getItem('authToken');
  };

  // Enhanced user data extraction with better field mapping
  const extractUserInfo = (userObj) => {
    if (!userObj) return null;
    
    // Handle different possible API response structures
    const userInfo = {
      username: userObj.username || userObj.name || userObj.user_name || userObj.displayName || 'User',
      email: userObj.email || userObj.email_address || userObj.user_email || '',
      role: userObj.role || userObj.role_name || userObj.user_role || userObj.type || 'User'
    };

    // Log for debugging
    console.log('Extracted user info:', userInfo);
    console.log('Raw user object:', userObj);

    return userInfo;
  };

  // Reset user data when user changes
  useEffect(() => {
    setUserData(null);
    setLoading(true);
    setError(null);
  }, [user?.id]);

  // Fetch user data from API whenever user changes
  const fetchUserData = async () => {
    // If no user ID, use fallback data immediately
    if (!user?.id) {
      console.log('No user ID available, using provided user data');
      setUserData(user);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      console.log(`Fetching user data for ID: ${user.id}`);
      
      const token = getToken();
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${API_BASE_URL}/api/users/${user.id}`, {
        method: 'GET',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
      });

      if (response.ok) {
        const fetchedUserData = await response.json();
        console.log('Fetched user data:', fetchedUserData);
        
        // Handle different API response structures
        let actualUserData = fetchedUserData;
        if (fetchedUserData.user) {
          actualUserData = fetchedUserData.user;
        } else if (fetchedUserData.data) {
          actualUserData = fetchedUserData.data;
        }
        
        setUserData(actualUserData);
      } else {
        console.warn('Failed to fetch user details:', response.status);
        throw new Error(`Failed to fetch user data: ${response.status} ${response.statusText}`);
      }
    } catch (err) {
      console.error('Error fetching user data:', err);
      setError(err.message);
      // Fallback to the user prop if API fails
      setUserData(user);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserData();
  }, [user?.id]);

  // Detect screen size changes
  useEffect(() => {
    const checkScreenSize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (mobile) {
        setIsSidebarOpen(false);
        setSidebarMinimized(false);
      }
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Load dark mode preference from localStorage
  useEffect(() => {
    const savedDarkMode = localStorage.getItem('darkMode') === 'true';
    setDarkMode(savedDarkMode);
  }, []);

  // Save dark mode preference
  useEffect(() => {
    localStorage.setItem('darkMode', darkMode.toString());
  }, [darkMode]);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isUserMenuOpen && !event.target.closest('.user-menu-container')) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isUserMenuOpen]);

  // Auto-expand category containing active tab
  useEffect(() => {
    const activePath = location.pathname;
    const activeCategory = sidebarCategories.find(category => {
      if (category.type === 'single' && category.path === activePath) {
        return true;
      }
      if (category.type === 'category' && category.tabs.some(tab => tab.path === activePath)) {
        return true;
      }
      return false;
    });

    if (activeCategory && activeCategory.type === 'category') {
      setExpandedCategories(prev => new Set(prev).add(activeCategory.key));
    }
  }, [location.pathname]);

  const toggleSidebar = () => {
    if (isMobile) {
      setIsSidebarOpen(!isSidebarOpen);
    } else {
      setSidebarMinimized(!sidebarMinimized);
      if (sidebarMinimized) {
        setIsSidebarOpen(true);
      }
    }
  };

  const toggleUserMenu = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
  };

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
    setIsUserMenuOpen(false);
  };

  const toggleCategory = (categoryKey) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryKey)) {
        newSet.delete(categoryKey);
      } else {
        newSet.add(categoryKey);
      }
      return newSet;
    });
  };

  const handleNavigation = (path) => {
    navigate(path);
    if (isMobile) {
      setIsSidebarOpen(false);
    }
  };

  const handleLogout = () => {
    setIsUserMenuOpen(false);
    // Clear all user data and token on logout
    setUserData(null);
    setError(null);
    localStorage.removeItem('token');
    localStorage.removeItem('authToken');
    onLogout();
    navigate('/login');
  };

  const handleProfile = () => {
    setIsUserMenuOpen(false);
    navigate('/profile', { state: { user: userData || user } }); 
  };

  const handleSettings = () => {
    setIsUserMenuOpen(false);
    navigate('/settings');
  };

  const isTabActive = (path) => {
    return location.pathname === path;
  };

  const isCategoryActive = (category) => {
    if (category.type === 'single') {
      return isTabActive(category.path);
    }
    return category.tabs.some(tab => isTabActive(tab.path));
  };

  // Get user display information - Only use actual user data
  const getDisplayUser = () => {
    // Try to extract from userData first (API response)
    if (userData) {
      const extracted = extractUserInfo(userData);
      if (extracted) return extracted;
    }

    // Then try from user prop (initial login data)
    if (user) {
      const extracted = extractUserInfo(user);
      if (extracted) return extracted;
    }

    // No hardcoded fallback - return loading state
    return {
      username: 'Loading...',
      email: 'Loading...',
      role: 'Loading...'
    };
  };

  const displayUser = getDisplayUser();

  // Force refresh user data function
  const refreshUserData = async () => {
    await fetchUserData();
    setIsUserMenuOpen(false);
  };

  // Calculate sidebar width based on state
  const getSidebarWidth = () => {
    if (isMobile) return '100%';
    if (sidebarMinimized) return '70px';
    return '280px';
  };

  const getMainContentMargin = () => {
    if (isMobile || !isSidebarOpen) return '0';
    if (sidebarMinimized) return '70px';
    return '280px';
  };

  // Styles
  const layoutStyles = {
    minHeight: '100vh',
    backgroundColor: currentColors.background,
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    color: currentColors.text,
    transition: 'all 0.3s ease'
  };

  const topbarStyles = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    height: '60px',
    backgroundColor: '#024A59',
    color: 'white',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0 20px',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
    zIndex: 1000,
    transition: 'all 0.3s ease'
  };

  const sidebarStyles = {
    position: 'fixed',
    top: '60px',
    left: 0,
    width: getSidebarWidth(),
    height: 'calc(100vh - 60px)',
    backgroundColor: currentColors.surface,
    boxShadow: '2px 0 10px rgba(0, 0, 0, 0.1)',
    transition: 'all 0.3s ease',
    zIndex: 999,
    overflowY: 'auto',
    transform: isSidebarOpen ? 'translateX(0)' : 'translateX(-100%)',
    borderRight: `1px solid ${currentColors.border}`
  };

  const mainContentStyles = {
    flex: 1,
    transition: 'margin-left 0.3s ease',
    marginLeft: getMainContentMargin(),
    paddingTop: '60px',
    minHeight: 'calc(100vh - 60px)',
    backgroundColor: currentColors.background
  };

  const navLinkBaseStyles = {
    width: '100%',
    padding: sidebarMinimized && !isMobile ? '16px 0' : '14px 20px',
    background: 'none',
    border: 'none',
    textAlign: 'center',
    display: 'flex',
    alignItems: 'center',
    justifyContent: sidebarMinimized && !isMobile ? 'center' : 'flex-start',
    gap: '12px',
    cursor: 'pointer',
    transition: 'all 0.3s',
    color: currentColors.text,
    textDecoration: 'none',
    fontSize: '0.95rem',
    borderBottom: `1px solid ${currentColors.border}`,
    position: 'relative'
  };

  const navLinkActiveStyles = {
    ...navLinkBaseStyles,
    backgroundColor: `${currentColors.primary}20`,
    color: currentColors.primary,
    borderRight: `3px solid ${currentColors.primary}`,
    fontWeight: '600'
  };

  const categoryHeaderBaseStyles = {
    width: '100%',
    padding: sidebarMinimized && !isMobile ? '16px 0' : '14px 20px',
    background: 'none',
    border: 'none',
    textAlign: 'center',
    display: 'flex',
    alignItems: 'center',
    justifyContent: sidebarMinimized && !isMobile ? 'center' : 'space-between',
    cursor: 'pointer',
    transition: 'all 0.3s',
    color: currentColors.text,
    fontSize: '0.95rem',
    fontWeight: '500',
    borderBottom: `1px solid ${currentColors.border}`,
    position: 'relative'
  };

  const categoryHeaderActiveStyles = {
    ...categoryHeaderBaseStyles,
    backgroundColor: `${currentColors.primary}15`,
    color: currentColors.primary,
    borderRight: `3px solid ${currentColors.primary}`,
    fontWeight: '600'
  };

  const subTabStyles = {
    ...navLinkBaseStyles,
    paddingLeft: sidebarMinimized && !isMobile ? '0' : '52px',
    fontSize: '0.9rem',
    borderBottom: `1px solid ${currentColors.border}`,
    justifyContent: sidebarMinimized && !isMobile ? 'center' : 'flex-start'
  };

  const subTabActiveStyles = {
    ...subTabStyles,
    backgroundColor: `${currentColors.primary}15`,
    color: currentColors.primary,
    borderRight: `3px solid ${currentColors.primary}`,
    fontWeight: '500'
  };

  const tooltipStyles = {
    position: 'absolute',
    left: '100%',
    top: '50%',
    transform: 'translateY(-50%)',
    backgroundColor: currentColors.surface,
    color: currentColors.text,
    padding: '8px 12px',
    borderRadius: '6px',
    fontSize: '0.85rem',
    whiteSpace: 'nowrap',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    border: `1px solid ${currentColors.border}`,
    zIndex: 1002,
    marginLeft: '8px',
    fontWeight: '500'
  };

  const userMenuStyles = {
    position: 'relative',
    display: 'inline-block'
  };

  const userMenuButtonStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    background: 'none',
    border: 'none',
    color: 'white',
    cursor: 'pointer',
    padding: '8px 12px',
    borderRadius: '8px',
    fontSize: '0.9rem',
    transition: 'all 0.3s ease'
  };

  const userMenuDropdownStyles = {
    position: 'absolute',
    top: '100%',
    right: 0,
    backgroundColor: currentColors.surface,
    minWidth: '220px',
    borderRadius: '12px',
    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
    border: `1px solid ${currentColors.border}`,
    zIndex: 1001,
    marginTop: '8px',
    overflow: 'hidden',
    transition: 'all 0.3s ease'
  };

  const userMenuItemStyles = {
    width: '100%',
    padding: '12px 16px',
    background: 'none',
    border: 'none',
    textAlign: 'left',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontSize: '0.9rem',
    color: currentColors.text,
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    borderBottom: `1px solid ${currentColors.border}`
  };

  const userInfoStyles = {
    padding: '16px',
    backgroundColor: `${currentColors.primary}10`,
    borderBottom: `1px solid ${currentColors.border}`
  };

  const modeToggleButtonStyles = {
    position: 'fixed',
    bottom: '20px',
    right: '20px',
    width: '50px',
    height: '50px',
    borderRadius: '50%',
    backgroundColor: currentColors.primary,
    color: 'white',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1.2rem',
    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
    zIndex: 1000,
    transition: 'all 0.3s ease'
  };

  return (
    <div style={layoutStyles}>
      {/* Topbar */}
      <header style={topbarStyles}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <button 
            style={{
              background: 'none',
              border: 'none',
              color: 'white',
              fontSize: '1.3rem',
              cursor: 'pointer',
              padding: '8px',
              borderRadius: '6px',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '40px',
              height: '40px'
            }}
            onClick={toggleSidebar}
            onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.2)'}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
          >
            ☰
          </button>
          <h1 style={{ 
            fontSize: '1.5rem', 
            fontWeight: 700,
            color: 'white',
            textShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            DairyPro Manager
          </h1>
        </div>
        
        {/* User Menu */}
        <div className="user-menu-container" style={userMenuStyles}>
          <button 
            style={userMenuButtonStyles}
            onClick={toggleUserMenu}
            onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.2)'}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
          >
            <div style={{
              width: '38px',
              height: '38px',
              backgroundColor: 'rgba(255,255,255,0.2)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.1rem',
              border: '2px solid rgba(255,255,255,0.3)'
            }}>
              👤
            </div>
            <span style={{ fontWeight: 600, fontSize: '0.95rem' }}>
              {displayUser.username}
            </span>
            <span style={{ 
              fontSize: '0.7rem',
              transition: 'transform 0.3s ease',
              transform: isUserMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)'
            }}>
              ▼
            </span>
          </button>

          {/* User Dropdown Menu */}
          {isUserMenuOpen && (
            <div style={userMenuDropdownStyles}>
              <div style={userInfoStyles}>
                <div style={{ 
                  fontWeight: 700, 
                  fontSize: '1rem', 
                  marginBottom: '4px',
                  color: currentColors.primary
                }}>
                  {displayUser.username}
                </div>
                <div style={{ 
                  fontSize: '0.8rem', 
                  color: currentColors.textSecondary,
                  marginBottom: '6px'
                }}>
                  {displayUser.email || 'Email not available'}
                </div>
                <div style={{ 
                  fontSize: '0.75rem', 
                  color: currentColors.primary,
                  padding: '4px 12px',
                  backgroundColor: `${currentColors.primary}15`,
                  borderRadius: '16px',
                  display: 'inline-block',
                  fontWeight: '600',
                  border: `1px solid ${currentColors.primary}30`
                }}>
                  {displayUser.role}
                </div>
                
                {/* Status indicators */}
                <div style={{ marginTop: '8px' }}>
                  {loading && (
                    <div style={{ 
                      fontSize: '0.7rem', 
                      color: currentColors.primary,
                    }}>
                      🔄 Loading user data...
                    </div>
                  )}
                  {error && (
                    <div style={{ 
                      fontSize: '0.7rem', 
                      color: '#FF6B6B',
                    }}>
                      ⚠️ {error}
                    </div>
                  )}
                  {!loading && !error && userData && (
                    <div style={{ 
                      fontSize: '0.7rem', 
                      color: '#2E8B57',
                    }}>
                      ✅ Live data
                    </div>
                  )}
                </div>
              </div>

              <button 
                style={userMenuItemStyles}
                onClick={handleProfile}
                onMouseEnter={(e) => e.target.style.backgroundColor = currentColors.hover}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                <span style={{ fontSize: '1.1rem' }}>👤</span>
                <span>My Profile</span>
              </button>

              <button 
                style={userMenuItemStyles}
                onClick={refreshUserData}
                onMouseEnter={(e) => e.target.style.backgroundColor = currentColors.hover}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                <span style={{ fontSize: '1.1rem' }}>🔄</span>
                <span>Refresh Data</span>
              </button>

              <button 
                style={userMenuItemStyles}
                onClick={toggleDarkMode}
                onMouseEnter={(e) => e.target.style.backgroundColor = currentColors.hover}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                <span style={{ fontSize: '1.1rem' }}>
                  {darkMode ? '☀️' : '🌙'}
                </span>
                <span>{darkMode ? 'Light Mode' : 'Dark Mode'}</span>
              </button>

              <button 
                style={userMenuItemStyles}
                onClick={handleSettings}
                onMouseEnter={(e) => e.target.style.backgroundColor = currentColors.hover}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                <span style={{ fontSize: '1.1rem' }}>⚙️</span>
                <span>Settings</span>
              </button>

              <button 
                style={{
                  ...userMenuItemStyles,
                  color: '#FF6B6B',
                  borderBottom: 'none'
                }}
                onClick={handleLogout}
                onMouseEnter={(e) => e.target.style.backgroundColor = `${currentColors.primary}10`}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                <span style={{ fontSize: '1.1rem' }}>🚪</span>
                <span>Logout</span>
              </button>
            </div>
          )}
        </div>
      </header>

      <div style={{ display: 'flex' }}>
        {/* Sidebar */}
        <aside style={sidebarStyles}>
          <nav style={{ padding: '10px 0' }}>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {sidebarCategories.map((category) => (
                <li key={category.key} style={{ marginBottom: '0', position: 'relative' }}>
                  {category.type === 'single' ? (
                    // Single tab (no dropdown)
                    <button
                      style={isTabActive(category.path) ? navLinkActiveStyles : navLinkBaseStyles}
                      onClick={() => handleNavigation(category.path)}
                      onMouseEnter={(e) => {
                        if (!isTabActive(category.path)) {
                          e.target.style.backgroundColor = currentColors.hover;
                          e.target.style.color = currentColors.primary;
                        }
                        if (sidebarMinimized && !isMobile) {
                          setHoveredItem(category);
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isTabActive(category.path)) {
                          e.target.style.backgroundColor = 'transparent';
                          e.target.style.color = currentColors.text;
                        }
                        if (sidebarMinimized && !isMobile) {
                          setHoveredItem(null);
                        }
                      }}
                    >
                      <span style={{ 
                        fontSize: '1.2rem', 
                        width: '24px', 
                        textAlign: 'center',
                        filter: isTabActive(category.path) ? 'brightness(1.2)' : 'none'
                      }}>
                        {category.icon}
                      </span>
                      {(!sidebarMinimized || isMobile) && (
                        <span style={{ flex: 1, fontWeight: isTabActive(category.path) ? '600' : '400' }}>
                          {category.label}
                        </span>
                      )}
                    </button>
                  ) : (
                    // Category with dropdown
                    <>
                      <button
                        style={isCategoryActive(category) ? categoryHeaderActiveStyles : categoryHeaderBaseStyles}
                        onClick={() => toggleCategory(category.key)}
                        onMouseEnter={(e) => {
                          if (!isCategoryActive(category)) {
                            e.target.style.backgroundColor = currentColors.hover;
                            e.target.style.color = currentColors.primary;
                          }
                          if (sidebarMinimized && !isMobile) {
                            setHoveredItem(category);
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isCategoryActive(category)) {
                            e.target.style.backgroundColor = 'transparent';
                            e.target.style.color = currentColors.text;
                          }
                          if (sidebarMinimized && !isMobile) {
                            setHoveredItem(null);
                          }
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <span style={{ 
                            fontSize: '1.2rem', 
                            width: '24px', 
                            textAlign: 'center',
                            filter: isCategoryActive(category) ? 'brightness(1.2)' : 'none'
                          }}>
                            {category.icon}
                          </span>
                          {(!sidebarMinimized || isMobile) && (
                            <span style={{ 
                              flex: 1,
                              fontWeight: isCategoryActive(category) ? '600' : '500'
                            }}>
                              {category.label}
                            </span>
                          )}
                        </div>
                        {(!sidebarMinimized || isMobile) && (
                          <span style={{ 
                            transform: expandedCategories.has(category.key) ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.3s ease',
                            fontSize: '0.8rem'
                          }}>
                            ▼
                          </span>
                        )}
                      </button>
                      
                      {/* Sub-tabs */}
                      {expandedCategories.has(category.key) && (!sidebarMinimized || isMobile) && (
                        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                          {category.tabs.map((tab) => (
                            <li key={tab.key} style={{ position: 'relative' }}>
                              <button
                                style={isTabActive(tab.path) ? subTabActiveStyles : subTabStyles}
                                onClick={() => handleNavigation(tab.path)}
                                onMouseEnter={(e) => {
                                  if (!isTabActive(tab.path)) {
                                    e.target.style.backgroundColor = currentColors.hover;
                                    e.target.style.color = currentColors.primary;
                                  }
                                  if (sidebarMinimized && !isMobile) {
                                    setHoveredItem(tab);
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (!isTabActive(tab.path)) {
                                    e.target.style.backgroundColor = 'transparent';
                                    e.target.style.color = currentColors.text;
                                  }
                                  if (sidebarMinimized && !isMobile) {
                                    setHoveredItem(null);
                                  }
                                }}
                              >
                                <span style={{ 
                                  fontSize: '1.1rem', 
                                  width: '24px', 
                                  textAlign: 'center',
                                  filter: isTabActive(tab.path) ? 'brightness(1.2)' : 'none'
                                }}>
                                  {tab.icon}
                                </span>
                                {(!sidebarMinimized || isMobile) && (
                                  <span style={{ 
                                    flex: 1,
                                    fontWeight: isTabActive(tab.path) ? '500' : '400'
                                  }}>
                                    {tab.label}
                                  </span>
                                )}
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </>
                  )}

                  {/* Tooltip for minimized sidebar */}
                  {sidebarMinimized && !isMobile && hoveredItem === category && (
                    <div style={tooltipStyles}>
                      {category.label}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </nav>
        </aside>

        {/* Main Content */}
        <main style={mainContentStyles}>
          <div style={{ 
            padding: '20px',
            minHeight: 'calc(100vh - 100px)'
          }}>
            {children}
          </div>
        </main>
      </div>

      {/* Dark/Light Mode Toggle Button */}
      <button
        style={modeToggleButtonStyles}
        onClick={toggleDarkMode}
        onMouseEnter={(e) => {
          e.target.style.transform = 'scale(1.1)';
          e.target.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.3)';
        }}
        onMouseLeave={(e) => {
          e.target.style.transform = 'scale(1)';
          e.target.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.2)';
        }}
        title={darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
      >
        {darkMode ? '☀️' : '🌙'}
      </button>

      {/* Responsive Styles */}
      <style>
        {`
          @media (max-width: 768px) {
            .sidebar {
              width: 100% !important;
            }
            
            .main-content {
              margin-left: 0 !important;
            }
            
            .user-menu-button span:not(:first-child) {
              display: none;
            }
          }
          
          @media (max-width: 480px) {
            .topbar h1 {
              font-size: 1.2rem !important;
            }
            
            .user-info {
              padding: 12px !important;
            }
            
            .mode-toggle {
              bottom: 15px !important;
              right: 15px !important;
              width: 45px !important;
              height: 45px !important;
            }
          }
          
          /* Scrollbar Styling */
          ::-webkit-scrollbar {
            width: 6px;
          }
          
          ::-webkit-scrollbar-track {
            background: ${currentColors.background};
          }
          
          ::-webkit-scrollbar-thumb {
            background: ${currentColors.primary};
            border-radius: 3px;
          }
          
          ::-webkit-scrollbar-thumb:hover {
            background: ${currentColors.accent};
          }
        `}
      </style>
    </div>
  );
};

export default DashboardLayout;

// import React, { useState, useEffect } from 'react';
// import { useLocation, useNavigate } from 'react-router-dom';

// const DashboardLayout = ({ children, onLogout, user }) => {
//   const [isSidebarOpen, setIsSidebarOpen] = useState(true);
//   const [isMobile, setIsMobile] = useState(false);
//   const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
//   const [expandedCategories, setExpandedCategories] = useState(new Set());
//   const [darkMode, setDarkMode] = useState(false);
//   const [userData, setUserData] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [sidebarMinimized, setSidebarMinimized] = useState(false);
//   const [hoveredItem, setHoveredItem] = useState(null);
//   const location = useNavigate();
//   const navigate = useNavigate();

//   // Define categories with their tabs
//   const sidebarCategories = [
//     {
//       key: 'dashboard',
//       label: 'Dashboard',
//       icon: '📊',
//       path: '/dashboard',
//       type: 'single'
//     },
//     {
//       key: 'animals',
//       label: 'Animals',
//       icon: '🐄',
//       type: 'category',
//       tabs: [
//         { key: 'animals_draft', label: 'Draft', icon: '📝', path: '/animals/draft' },
//         { key: 'animals_active', label: 'Active', icon: '✅', path: '/animals/active' },
//         { key: 'animals_inactive', label: 'Inactive', icon: '🚫', path: '/animals/inactive' }
//       ]
//     },
//     {
//       key: 'production',
//       label: 'Production',
//       icon: '📦',
//       type: 'category',
//       tabs: [
//         { key: 'milking_sessions', label: 'Milking Sessions', icon: '🥛', path: '/milking-sessions' },
//         { key: 'production_batches', label: 'Production Batches', icon: '📦', path: '/production-batches' },
//         { key: 'calving_events', label: 'Calving Events', icon: '👶', path: '/calving-events' },
//         { key: 'reproduction_events', label: 'Reproduction', icon: '🔬', path: '/reproduction-events' },
//         { key: 'production_report', label: 'Production Report', icon: '📊', path: '/production_report' }
//       ]
//     },
//     {
//       key: 'health',
//       label: 'Health',
//       icon: '🏥',
//       type: 'category',
//       tabs: [
//         { key: 'health_records', label: 'Health Records', icon: '🏥', path: '/health-records' },
//         { key: 'medications', label: 'Medications', icon: '💊', path: '/medications' },
//         { key: 'medication_usage', label: 'Medication Usage', icon: '📋', path: '/medication-usage' }
//       ]
//     },
    
//     {
//   key: 'sales',
//   label: 'Sales',
//   icon: '💰',
//   type: 'category',
//   tabs: [
//     { key: 'customers', label: 'Customers', icon: '👥', path: '/customer-form' },
//     { key: 'products',  label: 'Products',  icon: '🛍️', path: '/product-form' },
//     { key: 'invoices',  label: 'Sales invoice', icon: '🧾', path: '/invoice-form' },
//     { key: 'receipts',  label: 'Receipts', icon: '💵', path: '/receipts' }
//   ]
//     },

//     {
//       key: 'hr',
//       label: 'HR',
//       icon: '👥',
//       type: 'category',
//       tabs: [
//         { key: 'hr', label: 'Employees', icon: '👥', path: '/employees' },
//         { key: 'payroll', label: 'Payroll', icon: '💰', path: '/payroll' }
//       ]
//     },
//     {
//       key: 'setups',
//       label: 'Setups',
//       icon: '⚙️',
//       type: 'category',
//       tabs: [
//         { key: 'locations', label: 'Locations', icon: '📍', path: '/locations' },
//         { key: 'payroll Setup', label: 'Payroll Setup', icon: '💰', path: '/payroll-setup' }
//       ]
//     },
//     {
//       key: 'admin',
//       label: 'Admin',
//       icon: '👥',
//       type: 'category',
//       tabs: [
//         { key: 'users', label: 'Users', icon: '👥', path: '/users' },
//         { key: 'roles', label: 'Roles', icon: '🔐', path: '/roles' },
//         { key: 'audit_logs', label: 'Audit Logs', icon: '📝', path: '/audit-logs' }
//       ]
//     }
//   ];

//   // Color scheme based on mode
//   const colors = {
//     light: {
//       primary: '#024A59', // Bootstrap Green
//       secondary: '#FFA916', // Orange
//       accent: '#4169E1', // Royal Blue
//       background: '#F8F9FA',
//       surface: '#FFFFFF',
//       text: '#2C3E50',
//       textSecondary: '#6C757D',
//       border: '#E9ECEF',
//       hover: '#F1F3F4'
//     },
//     dark: {
//       primary: '#024A59', // Same green for consistency
//       secondary: '#FFA916', // Light Orange
//       accent: '#64B5F6', // Light Blue
//       background: '#121212',
//       surface: '#1E1E1E',
//       text: '#E0E0E0',
//       textSecondary: '#B0B0B0',
//       border: '#333333',
//       hover: '#2A2A2A'
//     }
//   };

//   const currentColors = darkMode ? colors.dark : colors.light;

//   // Reset user data when user changes
//   useEffect(() => {
//     setUserData(null);
//     setLoading(true);
//     setError(null);
//   }, [user?.id]);

//   // Fetch user data from API whenever user changes
//   useEffect(() => {
//     const fetchUserData = async () => {
//       // If no user ID, use fallback data immediately
//       if (!user?.id) {
//         console.log('No user ID available, using fallback data');
//         setUserData(user);
//         setLoading(false);
//         return;
//       }

//       try {
//         setLoading(true);
//         setError(null);
//         console.log(`Fetching user data for ID: ${user.id}`);
        
//         // Add cache busting to prevent cached responses
//         const timestamp = new Date().getTime();
//         const response = await fetch(`https://dairy.server.statify.co.ke/api/users/${user.id}?t=${timestamp}`, {
//           method: 'GET',
//           headers: {
//             'Content-Type': 'application/json',
//             'Cache-Control': 'no-cache, no-store, must-revalidate',
//             'Pragma': 'no-cache',
//             'Expires': '0'
//           }
//         });
        
//         if (!response.ok) {
//           throw new Error(`Failed to fetch user data: ${response.status} ${response.statusText}`);
//         }
        
//         const fetchedUserData = await response.json();
//         console.log('Fetched user data:', fetchedUserData);
//         setUserData(fetchedUserData);
//       } catch (err) {
//         console.error('Error fetching user data:', err);
//         setError(err.message);
//         // Fallback to the user prop if API fails
//         setUserData(user);
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchUserData();
//   }, [user?.id]); // Only depend on user.id

//   // Detect screen size changes
//   useEffect(() => {
//     const checkScreenSize = () => {
//       const mobile = window.innerWidth <= 768;
//       setIsMobile(mobile);
//       if (mobile) {
//         setIsSidebarOpen(false);
//         setSidebarMinimized(false);
//       }
//     };

//     checkScreenSize();
//     window.addEventListener('resize', checkScreenSize);
    
//     return () => window.removeEventListener('resize', checkScreenSize);
//   }, []);

//   // Load dark mode preference from localStorage
//   useEffect(() => {
//     const savedDarkMode = localStorage.getItem('darkMode') === 'true';
//     setDarkMode(savedDarkMode);
//   }, []);

//   // Save dark mode preference
//   useEffect(() => {
//     localStorage.setItem('darkMode', darkMode.toString());
//   }, [darkMode]);

//   // Close user menu when clicking outside
//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (isUserMenuOpen && !event.target.closest('.user-menu-container')) {
//         setIsUserMenuOpen(false);
//       }
//     };

//     document.addEventListener('mousedown', handleClickOutside);
//     return () => document.removeEventListener('mousedown', handleClickOutside);
//   }, [isUserMenuOpen]);

//   // Auto-expand category containing active tab
//   useEffect(() => {
//     const activePath = location.pathname;
//     const activeCategory = sidebarCategories.find(category => {
//       if (category.type === 'single' && category.path === activePath) {
//         return true;
//       }
//       if (category.type === 'category' && category.tabs.some(tab => tab.path === activePath)) {
//         return true;
//       }
//       return false;
//     });

//     if (activeCategory && activeCategory.type === 'category') {
//       setExpandedCategories(prev => new Set(prev).add(activeCategory.key));
//     }
//   }, [location.pathname]);

//   const toggleSidebar = () => {
//     if (isMobile) {
//       setIsSidebarOpen(!isSidebarOpen);
//     } else {
//       setSidebarMinimized(!sidebarMinimized);
//       if (sidebarMinimized) {
//         setIsSidebarOpen(true);
//       }
//     }
//   };

//   const toggleUserMenu = () => {
//     setIsUserMenuOpen(!isUserMenuOpen);
//   };

//   const toggleDarkMode = () => {
//     setDarkMode(!darkMode);
//     setIsUserMenuOpen(false);
//   };

//   const toggleCategory = (categoryKey) => {
//     setExpandedCategories(prev => {
//       const newSet = new Set(prev);
//       if (newSet.has(categoryKey)) {
//         newSet.delete(categoryKey);
//       } else {
//         newSet.add(categoryKey);
//       }
//       return newSet;
//     });
//   };

//   const handleNavigation = (path) => {
//     navigate(path);
//     if (isMobile) {
//       setIsSidebarOpen(false);
//     }
//   };

//   const handleLogout = () => {
//     setIsUserMenuOpen(false);
//     // Clear all user data on logout
//     setUserData(null);
//     setError(null);
//     onLogout();
//     navigate('/login');
//   };

//   const handleProfile = () => {
//     setIsUserMenuOpen(false);
//     navigate('/profile', { state: { user: userData || user } }); 
//   };

//   const handleSettings = () => {
//     setIsUserMenuOpen(false);
//     navigate('/settings');
//   };

//   const isTabActive = (path) => {
//     return location.pathname === path;
//   };

//   const isCategoryActive = (category) => {
//     if (category.type === 'single') {
//       return isTabActive(category.path);
//     }
//     return category.tabs.some(tab => isTabActive(tab.path));
//   };

//   // Get user display information - ALWAYS use current data
//   const getDisplayUser = () => {
//     // Priority 1: Use fetched API data
//     if (userData) {
//       return {
//         username: userData.username || userData.name || 'User',
//         email: userData.email || 'No email available',
//         role: userData.role || user?.role || 'User'
//       };
//     }

//     // Priority 2: Use the current user prop (most recent)
//     if (user) {
//       return {
//         username: user.username || user.name || 'User',
//         email: user.email || 'No email available',
//         role: user.role || 'User'
//       };
//     }

//     // Fallback: Loading state
//     return {
//       username: 'Loading...',
//       email: 'Loading...',
//       role: 'User'
//     };
//   };

//   const displayUser = getDisplayUser();

//   // Force refresh user data function
//   const refreshUserData = async () => {
//     if (!user?.id) return;
    
//     try {
//       setLoading(true);
//       const timestamp = new Date().getTime();
//       const response = await fetch(`http://127.0.0.1:5055/api/users/${user.id}?t=${timestamp}`, {
//         headers: {
//           'Cache-Control': 'no-cache'
//         }
//       });
      
//       if (response.ok) {
//         const freshData = await response.json();
//         setUserData(freshData);
//         setError(null);
//       }
//     } catch (err) {
//       console.error('Error refreshing user data:', err);
//     } finally {
//       setLoading(false);
//     }
//   };

//   // Calculate sidebar width based on state
//   const getSidebarWidth = () => {
//     if (isMobile) return '100%';
//     if (sidebarMinimized) return '70px';
//     return '280px';
//   };

//   const getMainContentMargin = () => {
//     if (isMobile || !isSidebarOpen) return '0';
//     if (sidebarMinimized) return '70px';
//     return '280px';
//   };

//   // Styles
//   const layoutStyles = {
//     minHeight: '100vh',
//     backgroundColor: currentColors.background,
//     fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
//     color: currentColors.text,
//     transition: 'all 0.3s ease'
//   };

//   const topbarStyles = {
//     position: 'fixed',
//     top: 0,
//     left: 0,
//     right: 0,
//     height: '60px',
//     backgroundColor: '#198754', // Single green color
//     color: 'white',
//     display: 'flex',
//     justifyContent: 'space-between',
//     alignItems: 'center',
//     padding: '0 20px',
//     boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
//     zIndex: 1000,
//     transition: 'all 0.3s ease'
//   };

//   const sidebarStyles = {
//     position: 'fixed',
//     top: '60px',
//     left: 0,
//     width: getSidebarWidth(),
//     height: 'calc(100vh - 60px)',
//     backgroundColor: currentColors.surface,
//     boxShadow: '2px 0 10px rgba(0, 0, 0, 0.1)',
//     transition: 'all 0.3s ease',
//     zIndex: 999,
//     overflowY: 'auto',
//     transform: isSidebarOpen ? 'translateX(0)' : 'translateX(-100%)',
//     borderRight: `1px solid ${currentColors.border}`
//   };

//   const mainContentStyles = {
//     flex: 1,
//     transition: 'margin-left 0.3s ease',
//     marginLeft: getMainContentMargin(),
//     paddingTop: '60px',
//     minHeight: 'calc(100vh - 60px)',
//     backgroundColor: currentColors.background
//   };

//   const navLinkBaseStyles = {
//     width: '100%',
//     padding: sidebarMinimized && !isMobile ? '16px 0' : '14px 20px',
//     background: 'none',
//     border: 'none',
//     textAlign: 'center',
//     display: 'flex',
//     alignItems: 'center',
//     justifyContent: sidebarMinimized && !isMobile ? 'center' : 'flex-start',
//     gap: '12px',
//     cursor: 'pointer',
//     transition: 'all 0.3s',
//     color: currentColors.text,
//     textDecoration: 'none',
//     fontSize: '0.95rem',
//     borderBottom: `1px solid ${currentColors.border}`,
//     position: 'relative'
//   };

//   const navLinkActiveStyles = {
//     ...navLinkBaseStyles,
//     backgroundColor: `${currentColors.primary}20`,
//     color: currentColors.primary,
//     borderRight: `3px solid ${currentColors.primary}`,
//     fontWeight: '600'
//   };

//   const categoryHeaderBaseStyles = {
//     width: '100%',
//     padding: sidebarMinimized && !isMobile ? '16px 0' : '14px 20px',
//     background: 'none',
//     border: 'none',
//     textAlign: 'center',
//     display: 'flex',
//     alignItems: 'center',
//     justifyContent: sidebarMinimized && !isMobile ? 'center' : 'space-between',
//     cursor: 'pointer',
//     transition: 'all 0.3s',
//     color: currentColors.text,
//     fontSize: '0.95rem',
//     fontWeight: '500',
//     borderBottom: `1px solid ${currentColors.border}`,
//     position: 'relative'
//   };

//   const categoryHeaderActiveStyles = {
//     ...categoryHeaderBaseStyles,
//     backgroundColor: `${currentColors.primary}15`,
//     color: currentColors.primary,
//     borderRight: `3px solid ${currentColors.primary}`,
//     fontWeight: '600'
//   };

//   const subTabStyles = {
//     ...navLinkBaseStyles,
//     paddingLeft: sidebarMinimized && !isMobile ? '0' : '52px',
//     fontSize: '0.9rem',
//     borderBottom: `1px solid ${currentColors.border}`,
//     justifyContent: sidebarMinimized && !isMobile ? 'center' : 'flex-start'
//   };

//   const subTabActiveStyles = {
//     ...subTabStyles,
//     backgroundColor: `${currentColors.primary}15`,
//     color: currentColors.primary,
//     borderRight: `3px solid ${currentColors.primary}`,
//     fontWeight: '500'
//   };

//   const tooltipStyles = {
//     position: 'absolute',
//     left: '100%',
//     top: '50%',
//     transform: 'translateY(-50%)',
//     backgroundColor: currentColors.surface,
//     color: currentColors.text,
//     padding: '8px 12px',
//     borderRadius: '6px',
//     fontSize: '0.85rem',
//     whiteSpace: 'nowrap',
//     boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
//     border: `1px solid ${currentColors.border}`,
//     zIndex: 1002,
//     marginLeft: '8px',
//     fontWeight: '500'
//   };

//   const userMenuStyles = {
//     position: 'relative',
//     display: 'inline-block'
//   };

//   const userMenuButtonStyles = {
//     display: 'flex',
//     alignItems: 'center',
//     gap: '10px',
//     background: 'none',
//     border: 'none',
//     color: 'white',
//     cursor: 'pointer',
//     padding: '8px 12px',
//     borderRadius: '8px',
//     fontSize: '0.9rem',
//     transition: 'all 0.3s ease'
//   };

//   const userMenuDropdownStyles = {
//     position: 'absolute',
//     top: '100%',
//     right: 0,
//     backgroundColor: currentColors.surface,
//     minWidth: '220px',
//     borderRadius: '12px',
//     boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
//     border: `1px solid ${currentColors.border}`,
//     zIndex: 1001,
//     marginTop: '8px',
//     overflow: 'hidden',
//     transition: 'all 0.3s ease'
//   };

//   const userMenuItemStyles = {
//     width: '100%',
//     padding: '12px 16px',
//     background: 'none',
//     border: 'none',
//     textAlign: 'left',
//     cursor: 'pointer',
//     transition: 'all 0.2s ease',
//     fontSize: '0.9rem',
//     color: currentColors.text,
//     display: 'flex',
//     alignItems: 'center',
//     gap: '12px',
//     borderBottom: `1px solid ${currentColors.border}`
//   };

//   const userInfoStyles = {
//     padding: '16px',
//     backgroundColor: `${currentColors.primary}10`,
//     borderBottom: `1px solid ${currentColors.border}`
//   };

//   const modeToggleButtonStyles = {
//     position: 'fixed',
//     bottom: '20px',
//     right: '20px',
//     width: '50px',
//     height: '50px',
//     borderRadius: '50%',
//     backgroundColor: currentColors.primary,
//     color: 'white',
//     border: 'none',
//     cursor: 'pointer',
//     display: 'flex',
//     alignItems: 'center',
//     justifyContent: 'center',
//     fontSize: '1.2rem',
//     boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
//     zIndex: 1000,
//     transition: 'all 0.3s ease'
//   };

//   return (
//     <div style={layoutStyles}>
//       {/* Topbar */}
//       <header style={topbarStyles}>
//         <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
//           <button 
//             style={{
//               background: 'none',
//               border: 'none',
//               color: 'white',
//               fontSize: '1.3rem',
//               cursor: 'pointer',
//               padding: '8px',
//               borderRadius: '6px',
//               transition: 'all 0.3s ease',
//               display: 'flex',
//               alignItems: 'center',
//               justifyContent: 'center',
//               width: '40px',
//               height: '40px'
//             }}
//             onClick={toggleSidebar}
//             onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.2)'}
//             onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
//           >
//             ☰
//           </button>
//           <h1 style={{ 
//             fontSize: '1.5rem', 
//             fontWeight: 700,
//             color: 'white',
//             textShadow: '0 2px 4px rgba(0,0,0,0.1)'
//           }}>
//             DairyPro Manager
//           </h1>
//         </div>
        
//         {/* User Menu */}
//         <div className="user-menu-container" style={userMenuStyles}>
//           <button 
//             style={userMenuButtonStyles}
//             onClick={toggleUserMenu}
//             onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.2)'}
//             onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
//           >
//             <div style={{
//               width: '38px',
//               height: '38px',
//               backgroundColor: 'rgba(255,255,255,0.2)',
//               borderRadius: '50%',
//               display: 'flex',
//               alignItems: 'center',
//               justifyContent: 'center',
//               fontSize: '1.1rem',
//               border: '2px solid rgba(255,255,255,0.3)'
//             }}>
//               👤
//             </div>
//             <span style={{ fontWeight: 600, fontSize: '0.95rem' }}>
//               {displayUser.username}
//             </span>
//             <span style={{ 
//               fontSize: '0.7rem',
//               transition: 'transform 0.3s ease',
//               transform: isUserMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)'
//             }}>
//               ▼
//             </span>
//           </button>

//           {/* User Dropdown Menu */}
//           {isUserMenuOpen && (
//             <div style={userMenuDropdownStyles}>
//               <div style={userInfoStyles}>
//                 <div style={{ 
//                   fontWeight: 700, 
//                   fontSize: '1rem', 
//                   marginBottom: '4px',
//                   color: currentColors.primary
//                 }}>
//                   {displayUser.username}
//                 </div>
//                 <div style={{ 
//                   fontSize: '0.8rem', 
//                   color: currentColors.textSecondary,
//                   marginBottom: '6px'
//                 }}>
//                   {displayUser.email}
//                 </div>
//                 <div style={{ 
//                   fontSize: '0.75rem', 
//                   color: currentColors.primary,
//                   padding: '4px 12px',
//                   backgroundColor: `${currentColors.primary}15`,
//                   borderRadius: '16px',
//                   display: 'inline-block',
//                   fontWeight: '600',
//                   border: `1px solid ${currentColors.primary}30`
//                 }}>
//                   {displayUser.role}
//                 </div>
                
//                 {/* Status indicators */}
//                 <div style={{ marginTop: '8px' }}>
//                   {loading && (
//                     <div style={{ 
//                       fontSize: '0.7rem', 
//                       color: currentColors.primary,
//                     }}>
//                       🔄 Updating user data...
//                     </div>
//                   )}
//                   {error && (
//                     <div style={{ 
//                       fontSize: '0.7rem', 
//                       color: '#FF6B6B',
//                     }}>
//                       ⚠️ Using cached data
//                     </div>
//                   )}
//                   {!loading && !error && userData && (
//                     <div style={{ 
//                       fontSize: '0.7rem', 
//                       color: '#2E8B57',
//                     }}>
//                       ✅ Live data
//                     </div>
//                   )}
//                 </div>
//               </div>

//               <button 
//                 style={userMenuItemStyles}
//                 onClick={handleProfile}
//                 onMouseEnter={(e) => e.target.style.backgroundColor = currentColors.hover}
//                 onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
//               >
//                 <span style={{ fontSize: '1.1rem' }}>👤</span>
//                 <span>My Profile</span>
//               </button>

//               <button 
//                 style={userMenuItemStyles}
//                 onClick={() => {
//                   refreshUserData();
//                   setIsUserMenuOpen(false);
//                 }}
//                 onMouseEnter={(e) => e.target.style.backgroundColor = currentColors.hover}
//                 onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
//               >
//                 <span style={{ fontSize: '1.1rem' }}>🔄</span>
//                 <span>Refresh Data</span>
//               </button>

//               <button 
//                 style={userMenuItemStyles}
//                 onClick={toggleDarkMode}
//                 onMouseEnter={(e) => e.target.style.backgroundColor = currentColors.hover}
//                 onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
//               >
//                 <span style={{ fontSize: '1.1rem' }}>
//                   {darkMode ? '☀️' : '🌙'}
//                 </span>
//                 <span>{darkMode ? 'Light Mode' : 'Dark Mode'}</span>
//               </button>

//               <button 
//                 style={userMenuItemStyles}
//                 onClick={handleSettings}
//                 onMouseEnter={(e) => e.target.style.backgroundColor = currentColors.hover}
//                 onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
//               >
//                 <span style={{ fontSize: '1.1rem' }}>⚙️</span>
//                 <span>Settings</span>
//               </button>

//               <button 
//                 style={{
//                   ...userMenuItemStyles,
//                   color: '#FF6B6B',
//                   borderBottom: 'none'
//                 }}
//                 onClick={handleLogout}
//                 onMouseEnter={(e) => e.target.style.backgroundColor = `${currentColors.primary}10`}
//                 onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
//               >
//                 <span style={{ fontSize: '1.1rem' }}>🚪</span>
//                 <span>Logout</span>
//               </button>
//             </div>
//           )}
//         </div>
//       </header>

//       <div style={{ display: 'flex' }}>
//         {/* Sidebar */}
//         <aside style={sidebarStyles}>
//           <nav style={{ padding: '10px 0' }}>
//             <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
//               {sidebarCategories.map((category) => (
//                 <li key={category.key} style={{ marginBottom: '0', position: 'relative' }}>
//                   {category.type === 'single' ? (
//                     // Single tab (no dropdown)
//                     <button
//                       style={isTabActive(category.path) ? navLinkActiveStyles : navLinkBaseStyles}
//                       onClick={() => handleNavigation(category.path)}
//                       onMouseEnter={(e) => {
//                         if (!isTabActive(category.path)) {
//                           e.target.style.backgroundColor = currentColors.hover;
//                           e.target.style.color = currentColors.primary;
//                         }
//                         if (sidebarMinimized && !isMobile) {
//                           setHoveredItem(category);
//                         }
//                       }}
//                       onMouseLeave={(e) => {
//                         if (!isTabActive(category.path)) {
//                           e.target.style.backgroundColor = 'transparent';
//                           e.target.style.color = currentColors.text;
//                         }
//                         if (sidebarMinimized && !isMobile) {
//                           setHoveredItem(null);
//                         }
//                       }}
//                     >
//                       <span style={{ 
//                         fontSize: '1.2rem', 
//                         width: '24px', 
//                         textAlign: 'center',
//                         filter: isTabActive(category.path) ? 'brightness(1.2)' : 'none'
//                       }}>
//                         {category.icon}
//                       </span>
//                       {(!sidebarMinimized || isMobile) && (
//                         <span style={{ flex: 1, fontWeight: isTabActive(category.path) ? '600' : '400' }}>
//                           {category.label}
//                         </span>
//                       )}
//                     </button>
//                   ) : (
//                     // Category with dropdown
//                     <>
//                       <button
//                         style={isCategoryActive(category) ? categoryHeaderActiveStyles : categoryHeaderBaseStyles}
//                         onClick={() => toggleCategory(category.key)}
//                         onMouseEnter={(e) => {
//                           if (!isCategoryActive(category)) {
//                             e.target.style.backgroundColor = currentColors.hover;
//                             e.target.style.color = currentColors.primary;
//                           }
//                           if (sidebarMinimized && !isMobile) {
//                             setHoveredItem(category);
//                           }
//                         }}
//                         onMouseLeave={(e) => {
//                           if (!isCategoryActive(category)) {
//                             e.target.style.backgroundColor = 'transparent';
//                             e.target.style.color = currentColors.text;
//                           }
//                           if (sidebarMinimized && !isMobile) {
//                             setHoveredItem(null);
//                           }
//                         }}
//                       >
//                         <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
//                           <span style={{ 
//                             fontSize: '1.2rem', 
//                             width: '24px', 
//                             textAlign: 'center',
//                             filter: isCategoryActive(category) ? 'brightness(1.2)' : 'none'
//                           }}>
//                             {category.icon}
//                           </span>
//                           {(!sidebarMinimized || isMobile) && (
//                             <span style={{ 
//                               flex: 1,
//                               fontWeight: isCategoryActive(category) ? '600' : '500'
//                             }}>
//                               {category.label}
//                             </span>
//                           )}
//                         </div>
//                         {(!sidebarMinimized || isMobile) && (
//                           <span style={{ 
//                             transform: expandedCategories.has(category.key) ? 'rotate(180deg)' : 'rotate(0deg)',
//                             transition: 'transform 0.3s ease',
//                             fontSize: '0.8rem'
//                           }}>
//                             ▼
//                           </span>
//                         )}
//                       </button>
                      
//                       {/* Sub-tabs */}
//                       {expandedCategories.has(category.key) && (!sidebarMinimized || isMobile) && (
//                         <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
//                           {category.tabs.map((tab) => (
//                             <li key={tab.key} style={{ position: 'relative' }}>
//                               <button
//                                 style={isTabActive(tab.path) ? subTabActiveStyles : subTabStyles}
//                                 onClick={() => handleNavigation(tab.path)}
//                                 onMouseEnter={(e) => {
//                                   if (!isTabActive(tab.path)) {
//                                     e.target.style.backgroundColor = currentColors.hover;
//                                     e.target.style.color = currentColors.primary;
//                                   }
//                                   if (sidebarMinimized && !isMobile) {
//                                     setHoveredItem(tab);
//                                   }
//                                 }}
//                                 onMouseLeave={(e) => {
//                                   if (!isTabActive(tab.path)) {
//                                     e.target.style.backgroundColor = 'transparent';
//                                     e.target.style.color = currentColors.text;
//                                   }
//                                   if (sidebarMinimized && !isMobile) {
//                                     setHoveredItem(null);
//                                   }
//                                 }}
//                               >
//                                 <span style={{ 
//                                   fontSize: '1.1rem', 
//                                   width: '24px', 
//                                   textAlign: 'center',
//                                   filter: isTabActive(tab.path) ? 'brightness(1.2)' : 'none'
//                                 }}>
//                                   {tab.icon}
//                                 </span>
//                                 {(!sidebarMinimized || isMobile) && (
//                                   <span style={{ 
//                                     flex: 1,
//                                     fontWeight: isTabActive(tab.path) ? '500' : '400'
//                                   }}>
//                                     {tab.label}
//                                   </span>
//                                 )}
//                               </button>
//                             </li>
//                           ))}
//                         </ul>
//                       )}
//                     </>
//                   )}

//                   {/* Tooltip for minimized sidebar */}
//                   {sidebarMinimized && !isMobile && hoveredItem === category && (
//                     <div style={tooltipStyles}>
//                       {category.label}
//                     </div>
//                   )}
//                 </li>
//               ))}
//             </ul>
//           </nav>
//         </aside>

//         {/* Main Content */}
//         <main style={mainContentStyles}>
//           <div style={{ 
//             padding: '20px',
//             minHeight: 'calc(100vh - 100px)'
//           }}>
//             {children}
//           </div>
//         </main>
//       </div>

//       {/* Dark/Light Mode Toggle Button */}
//       <button
//         style={modeToggleButtonStyles}
//         onClick={toggleDarkMode}
//         onMouseEnter={(e) => {
//           e.target.style.transform = 'scale(1.1)';
//           e.target.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.3)';
//         }}
//         onMouseLeave={(e) => {
//           e.target.style.transform = 'scale(1)';
//           e.target.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.2)';
//         }}
//         title={darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
//       >
//         {darkMode ? '☀️' : '🌙'}
//       </button>

//       {/* Responsive Styles */}
//       <style>
//         {`
//           @media (max-width: 768px) {
//             .sidebar {
//               width: 100% !important;
//             }
            
//             .main-content {
//               margin-left: 0 !important;
//             }
            
//             .user-menu-button span:not(:first-child) {
//               display: none;
//             }
//           }
          
//           @media (max-width: 480px) {
//             .topbar h1 {
//               font-size: 1.2rem !important;
//             }
            
//             .user-info {
//               padding: 12px !important;
//             }
            
//             .mode-toggle {
//               bottom: 15px !important;
//               right: 15px !important;
//               width: 45px !important;
//               height: 45px !important;
//             }
//           }
          
//           /* Scrollbar Styling */
//           ::-webkit-scrollbar {
//             width: 6px;
//           }
          
//           ::-webkit-scrollbar-track {
//             background: ${currentColors.background};
//           }
          
//           ::-webkit-scrollbar-thumb {
//             background: ${currentColors.primary};
//             border-radius: 3px;
//           }
          
//           ::-webkit-scrollbar-thumb:hover {
//             background: ${currentColors.accent};
//           }
//         `}
//       </style>
//     </div>
//   );
// };

// export default DashboardLayout;








